/**
 * Deal Notifications Helper
 * Creates persistent notifications for deals with expected_close_date
 */

import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays, startOfDay, isBefore, format as formatDate, subDays, isAfter } from 'date-fns';

export interface CreateDealNotificationsInput {
  dealId: string;
  leadId: string | null;
  workspaceId: string;
  userId: string;
  expectedCloseDate?: string | null; // ISO or yyyy-mm-dd
  dealTitle: string;
  dealValue?: number | null;
}

/**
 * Create notifications for a deal.
 * Always inserts at least one immediate notification:
 *  - type='deal_in_play', scheduled_for=null
 *
 * If expectedCloseDate is set and is in the future, also inserts scheduled reminders:
 *  - T-7, T-3, T-1, and T0 (same day at 09:00 local when in the future; otherwise immediate)
 */
export async function createDealNotifications(input: CreateDealNotificationsInput): Promise<void> {
  const { dealId, leadId, workspaceId, userId, expectedCloseDate, dealTitle, dealValue } = input;

  try {
    const now = new Date();
    const closeDate = expectedCloseDate ? parseFlexibleDate(expectedCloseDate) : null;
    const closeAt9amLocal = closeDate ? setLocalTime(closeDate, 9, 0) : null;
    const today = startOfDay(now);
    const closeDay = closeDate ? startOfDay(closeDate) : null;
    const daysUntil = closeDay ? differenceInDays(closeDay, today) : null;

    // Build desired notification rows (we dedupe against existing rows for this deal+user)
    const desired: Array<{
      type: string;
      title: string;
      body: string;
      scheduled_for: string | null;
    }> = [];

    // 1) Immediate "deal in play" (ALWAYS)
    desired.push({
      type: 'deal_in_play',
      title: 'Deal in play',
      body: closeDate
        ? `Close date set: ${formatDate(closeDate, 'MMM d, yyyy')}.`
        : 'Deal created. Add an expected close date to activate reminders.',
      scheduled_for: null,
    });

    // 2) Scheduled reminders only if close date exists and is in the future
    if (closeAt9amLocal && isAfter(closeAt9amLocal, now) && daysUntil !== null) {
      const reminderBody = (prefix: string) =>
        `${dealTitle}${dealValue ? ` (${formatCurrency(dealValue)})` : ''} ${prefix} ${formatDate(closeDate!, 'MMM d, yyyy')}.`;

      const scheduleAt = (d: Date) => (isBefore(now, d) ? d.toISOString() : null);

      // T-7 (if >= 7 days away)
      if (daysUntil >= 7) {
        const t7 = setLocalTime(subDays(closeDate!, 7), 9, 0);
        if (isBefore(now, t7)) {
          desired.push({
            type: 'deal_close_reminder',
            title: 'Deal in play: closes in 7 days',
            body: reminderBody('closes on'),
            scheduled_for: t7.toISOString(),
          });
        }
      }

      // T-3 (if >= 3 days away)
      if (daysUntil >= 3) {
        const t3 = setLocalTime(subDays(closeDate!, 3), 9, 0);
        if (isBefore(now, t3)) {
          desired.push({
            type: 'deal_close_reminder',
            title: 'Deal in play: closes in 3 days',
            body: reminderBody('closes on'),
            scheduled_for: t3.toISOString(),
          });
        }
      }

      // T-1 (if >= 1 day away)
      if (daysUntil >= 1) {
        const t1 = setLocalTime(subDays(closeDate!, 1), 9, 0);
        if (isBefore(now, t1)) {
          desired.push({
            type: 'deal_close_reminder',
            title: 'Deal in play: closes tomorrow',
            body: `${dealTitle}${dealValue ? ` (${formatCurrency(dealValue)})` : ''} closes tomorrow.`,
            scheduled_for: t1.toISOString(),
          });
        }
      }

      // T0 (same-day 09:00 local; if already passed, immediate)
      desired.push({
        type: 'deal_close_reminder',
        title: 'Deal in play: closes today',
        body: `${dealTitle}${dealValue ? ` (${formatCurrency(dealValue)})` : ''} closes today.`,
        scheduled_for: scheduleAt(closeAt9amLocal),
      });
    }

    // Dedupe against existing notifications for this deal+user (avoid duplicates)
    const { data: existing, error: existingError } = await supabase
      .from('notifications')
      .select('id,type,scheduled_for')
      .eq('deal_id', dealId)
      .eq('user_id', userId);

    if (existingError) {
      // Useful to diagnose RLS mis-scoping in production
      logger.error('[createDealNotifications] Failed to fetch existing notifications', existingError, {
        dealId,
        workspaceId,
        userId,
      });
    }

    const existingKeys = new Set(
      (existing || []).map((n: any) => `${n.type}::${n.scheduled_for ?? 'null'}`)
    );

    const rowsToInsert = desired.filter((n) => !existingKeys.has(`${n.type}::${n.scheduled_for ?? 'null'}`));

    if (rowsToInsert.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[createDealNotifications] Nothing to insert (already present)', { dealId, workspaceId, userId });
      }
      return;
    }

    const insertRows = rowsToInsert.map((notif) => ({
      workspace_id: workspaceId,
      user_id: userId,
      type: notif.type,
      title: notif.title,
      body: notif.body,
      scheduled_for: notif.scheduled_for,
      lead_id: leadId,
      deal_id: dealId,
      is_read: false,
      meta: {
        dealValue,
        expectedCloseDate: closeDate ? closeDate.toISOString() : null,
      },
    }));

    const { error: insertError } = await supabase.from('notifications').insert(insertRows as any);

    if (insertError) {
      // Full Supabase error logging (common cause: RLS / wrong user_id)
      logger.error('[createDealNotifications] Insert failed', insertError, {
        dealId,
        workspaceId,
        userId,
        rowsAttempted: insertRows.length,
      });
      if (process.env.NODE_ENV === 'development') {
        console.warn('[createDealNotifications][dev] Insert failed. Check RLS/user_id/workspace_id.', insertError);
      }
      return;
    }

    logger.info('[createDealNotifications] Created notifications', { 
      dealId, 
      count: rowsToInsert.length,
      workspaceId 
    });
  } catch (error) {
    logger.error('[createDealNotifications] Failed to create deal notifications', error, { dealId, workspaceId });
    // Don't throw - notifications are non-critical
  }
}

/**
 * Delete all notifications for a deal (when deal is won/lost or expected_close_date is removed)
 */
export async function deleteDealNotifications(dealId: string, workspaceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('deal_id', dealId)
      .eq('workspace_id', workspaceId);

    if (error) {
      throw error;
    }
    logger.info('[deleteDealNotifications] Deleted notifications', { dealId, workspaceId });
  } catch (error) {
    logger.error('[deleteDealNotifications] Failed to delete notifications', error, { dealId, workspaceId });
    // Don't throw - cleanup is non-critical
  }
}

// Helper to format currency (simple version, can be improved)
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function parseFlexibleDate(input: string): Date {
  // Accept ISO strings or yyyy-mm-dd from <input type="date" />
  const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d);
  }
  return new Date(input);
}

function setLocalTime(date: Date, hours: number, minutes: number): Date {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

